import { type FC } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';

import { QUANTITY } from '../../constants';

interface EditableCellProps {
  value: number | undefined;
  loading: boolean;
  onChange: (newValue: number) => void;
}

export const EditableCell: FC<EditableCellProps> = ({
  value,
  loading,
  onChange,
}) => {
  const handleChange = (_: unknown, newValue: string | null) => {
    if (newValue) {
      const num = parseInt(newValue);
      if (!isNaN(num) && num >= 0) {
        onChange(num);
      }
    }
  };

  const handleInputChange = (_: unknown, newInputValue: string) => {
    if (newInputValue === '') {
      onChange(0);
      return;
    }
    const num = parseInt(newInputValue);
    if (!isNaN(num) && num >= 0) {
      onChange(num);
    }
  };

  return (
    <Autocomplete
      freeSolo
      forcePopupIcon
      disableClearable
      disabled={loading}
      value={value?.toString() || '0'}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={QUANTITY.map(q => q.value)}
      popupIcon={<KeyboardArrowDown />}
      sx={{
        width: { xs: '2.8125rem', sm: '3.5rem', md: '4rem' },
      }}
      slotProps={{
        popper: {
          sx: {
            '& .MuiPaper-root': {
              borderRadius: '0.5rem',
              marginTop: '0.25rem',
              boxShadow: '0px 1px 3px 0px #0000001A, 0px 1px 2px 0px #0000001A',
            },
            '& .MuiAutocomplete-listbox': {
              padding: '0.25rem',
              maxHeight: '12rem',
              '& .MuiAutocomplete-option': {
                fontSize: '0.75rem',
                padding: '0.375rem 0.5rem',
                minHeight: 'auto',
                borderRadius: '0.25rem',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
                '&.Mui-focused': {
                  backgroundColor: '#f0f0f0',
                },
                '&[aria-selected="true"]': {
                  backgroundColor: '#e8e8e8',
                  fontWeight: 500,
                },
              },
            },
          },
        },
      }}
      renderInput={params => (
        <TextField
          {...params}
          type='number'
          slotProps={{
            input: {
              ...params.InputProps,
              inputProps: {
                ...params.inputProps,
                min: 0,
              },
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              height: { xs: '1.875rem', sm: '2.25rem', md: '2.5rem' },
              fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
              backgroundColor: 'white',
              borderRadius: '0.5rem',
              border: '1px solid rgba(0, 0, 0, 0.2)',
              paddingLeft: { xs: '0.375rem', sm: '0.5rem', md: '0.5rem' },
              paddingRight: '0.125rem',
              '& fieldset': {
                border: 'none',
              },
              '&:hover': {
                borderColor: 'rgba(0, 0, 0, 0.3)',
              },
              '&.Mui-focused': {
                borderColor: '#007AFF',
                boxShadow: '0 0 0 2px rgba(0, 122, 255, 0.1)',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: '0',
              textAlign: 'left',
              paddingLeft: { xs: '0.125rem', sm: '0.125rem', md: '0.125rem' },
              '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              '&[type=number]': {
                MozAppearance: 'textfield',
              },
            },
            '& .MuiAutocomplete-endAdornment': {
              right: { xs: '0.125rem', sm: '0.25rem' },
              '& .MuiIconButton-root': {
                padding: { xs: '0.125rem', sm: '0.25rem' },
                color: '#8E8E93',
                '& .MuiSvgIcon-root': {
                  fontSize: { xs: '0.875rem', sm: '1rem', md: '1.25rem' },
                },
              },
            },
          }}
        />
      )}
    />
  );
};
